/**
 * useDemoDebts Hook
 * Provides demo debt management for anonymous users
 * All data stays in localStorage/React state - never persisted to backend
 */

import { useState, useCallback, useEffect } from 'react';
import { analytics } from '../lib/posthog';

// Demo seed data - realistic UK debts
const DEMO_SEED_DEBTS = [
  {
    id: 'demo-credit-card',
    name: 'Credit Card',
    balance: 1200,
    originalAmount: 1500,
    interestRate: 19.99,
    minPayment: 50,
    limit: 1500,
    order: 1,
    createdAt: new Date('2024-01-15').toISOString(),
    isDemo: true
  },
  {
    id: 'demo-car-loan',
    name: 'Car Loan', 
    balance: 7500,
    originalAmount: 12000,
    interestRate: 4.9,
    minPayment: 150,
    order: 2,
    createdAt: new Date('2023-06-01').toISOString(),
    isDemo: true
  },
  {
    id: 'demo-personal-loan',
    name: 'Personal Loan',
    balance: 2500,
    originalAmount: 3000, 
    interestRate: 7.5,
    minPayment: 80,
    order: 3,
    createdAt: new Date('2024-03-10').toISOString(),
    isDemo: true
  }
];

const DEMO_STORAGE_KEY = 'trysnowball_demo_debts';

export const useDemoDebts = () => {
  const [demoDebts, setDemoDebts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Initialize demo debts from localStorage or use seed data
  useEffect(() => {
    try {
      const storedDemo = localStorage.getItem(DEMO_STORAGE_KEY);
      if (storedDemo) {
        const parsed = JSON.parse(storedDemo);
        setDemoDebts(parsed);
      } else {
        // First visit - use seed data
        setDemoDebts(DEMO_SEED_DEBTS);
        localStorage.setItem(DEMO_STORAGE_KEY, JSON.stringify(DEMO_SEED_DEBTS));
        
        // Track demo mode initialization
        analytics.track('demo_mode_initialized', {
          seed_debts_count: DEMO_SEED_DEBTS.length,
          total_demo_balance: DEMO_SEED_DEBTS.reduce((sum, debt) => sum + debt.balance, 0),
          timestamp: new Date().toISOString()
        });
      }
    } catch (error) {
      console.warn('Error loading demo debts, using seed data:', error);
      setDemoDebts(DEMO_SEED_DEBTS);
    }
    
    setLoading(false);
  }, []);

  // Save demo debts to localStorage
  const saveDemoDebts = useCallback((debts) => {
    try {
      localStorage.setItem(DEMO_STORAGE_KEY, JSON.stringify(debts));
      setDemoDebts(debts);
    } catch (error) {
      console.warn('Error saving demo debts:', error);
    }
  }, []);

  // Add demo debt
  const addDemoDebt = useCallback((debtData) => {
    const newDebt = {
      ...debtData,
      id: `demo-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      createdAt: new Date().toISOString(),
      isDemo: true,
      order: demoDebts.length + 1
    };

    const updatedDebts = [...demoDebts, newDebt];
    saveDemoDebts(updatedDebts);

    // Track demo debt addition
    analytics.track('demo_debt_added', {
      debt_name: newDebt.name,
      debt_balance: newDebt.balance,
      debt_rate: newDebt.interestRate,
      total_demo_debts: updatedDebts.length,
      timestamp: new Date().toISOString()
    });

    return newDebt;
  }, [demoDebts, saveDemoDebts]);

  // Update demo debt
  const updateDemoDebt = useCallback((debtId, updates) => {
    const updatedDebts = demoDebts.map(debt => 
      debt.id === debtId 
        ? { ...debt, ...updates, updatedAt: new Date().toISOString() }
        : debt
    );

    saveDemoDebts(updatedDebts);

    // Track demo debt modification
    analytics.track('demo_debt_updated', {
      debt_id: debtId,
      fields_updated: Object.keys(updates),
      timestamp: new Date().toISOString()
    });

    return updatedDebts.find(debt => debt.id === debtId);
  }, [demoDebts, saveDemoDebts]);

  // Delete demo debt
  const deleteDemoDebt = useCallback((debtId) => {
    const debtToDelete = demoDebts.find(debt => debt.id === debtId);
    const updatedDebts = demoDebts.filter(debt => debt.id !== debtId);
    
    saveDemoDebts(updatedDebts);

    // Track demo debt deletion
    analytics.track('demo_debt_deleted', {
      debt_id: debtId,
      debt_name: debtToDelete?.name,
      remaining_debts: updatedDebts.length,
      timestamp: new Date().toISOString()
    });
  }, [demoDebts, saveDemoDebts]);

  // Reorder demo debts (for snowball strategy)
  const reorderDemoDebts = useCallback((newOrder) => {
    const reorderedDebts = newOrder.map((debt, index) => ({
      ...debt,
      order: index + 1
    }));
    
    saveDemoDebts(reorderedDebts);

    // Track reordering
    analytics.track('demo_debts_reordered', {
      debts_count: reorderedDebts.length,
      new_order: reorderedDebts.map(d => ({ id: d.id, name: d.name, order: d.order })),
      timestamp: new Date().toISOString()
    });
  }, [saveDemoDebts]);

  // Clear all demo data (reset to seed)
  const clearDemoData = useCallback(() => {
    localStorage.removeItem(DEMO_STORAGE_KEY);
    setDemoDebts(DEMO_SEED_DEBTS);
    localStorage.setItem(DEMO_STORAGE_KEY, JSON.stringify(DEMO_SEED_DEBTS));

    analytics.track('demo_data_reset', {
      previous_debts_count: demoDebts.length,
      timestamp: new Date().toISOString()
    });
  }, [demoDebts.length]);

  // Load demo data (for compatibility with existing components)
  const loadDemoData = useCallback(() => {
    clearDemoData(); // Reset to seed data
  }, [clearDemoData]);

  // Calculate totals for demo debts
  const totalDemoBalance = demoDebts.reduce((sum, debt) => sum + (debt.balance || 0), 0);
  const totalMinPayments = demoDebts.reduce((sum, debt) => sum + (debt.minPayment || 0), 0);

  return {
    // Data
    debts: demoDebts,
    loading,
    error: null, // Demo mode doesn't have backend errors
    
    // Actions
    addDebt: addDemoDebt,
    updateDebt: updateDemoDebt,
    deleteDebt: deleteDemoDebt,
    saveDebt: updateDemoDebt, // Alias for compatibility
    reorderDebts: reorderDemoDebts,
    
    // Utilities
    loadDemoData,
    clearAllData: clearDemoData,
    
    // Computed values
    totalBalance: totalDemoBalance,
    totalMinPayments,
    
    // Demo mode flag
    isDemoMode: true
  };
};

// Utility function to check if debt is demo
export const isDemoDebt = (debt) => {
  return debt?.isDemo === true || debt?.id?.startsWith('demo-');
};

// Utility to convert demo debts to real debt format (for signup carry-forward)
export const convertDemoDebtsToReal = (demoDebts) => {
  return demoDebts.map(debt => {
    const { id, isDemo, createdAt, updatedAt, ...realDebt } = debt;
    return {
      ...realDebt,
      // Generate new ID for real debt
      id: undefined, // Let backend assign
      createdAt: undefined,
      updatedAt: undefined
    };
  });
};

export default useDemoDebts;